import { useEffect, useRef, type RefObject } from "react";
import { useLocation } from "react-router-dom";

/**
 * 포커스 수명 주기 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 규칙을 화면마다 다시 쓰면 한 화면만 고쳐지고 나머지는 옛 동작으로 남습니다.
 * 세 가지 상황의 정답을 여기 한 곳에 둡니다.
 *
 *   라우트 변경  본문으로 포커스를 옮깁니다. **경로가 바뀔 때만**입니다 —
 *               자동 갱신·필터 변경(쿼리)은 포커스를 건드리지 않습니다.
 *   모달 닫힘    연 곳으로 정확히 한 번 돌려놓습니다.
 *   비동기 오류  오류가 **처음 나타난 순간에만** 알림으로 옮깁니다. 재렌더마다
 *               옮기면 사용자가 입력하던 곳에서 계속 튕겨 나갑니다.
 */

/**
 * 라우트가 바뀌면 본문 대상으로 포커스를 옮깁니다.
 *
 * 첫 마운트에는 옮기지 않습니다 — 페이지를 열자마자 포커스를 훔치면 건너뛰기
 * 링크가 첫 Tab 정지점이라는 규약이 깨집니다. 판정 키는 **pathname 하나**입니다.
 * 검색 파라미터는 필터·범위·자동 갱신이 쓰는 자리라 여기 들어오면 안 됩니다.
 */
export function useRouteFocus<T extends HTMLElement>(): RefObject<T> {
  const { pathname } = useLocation();
  const target = useRef<T>(null);
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (previous.current === null) {
      previous.current = pathname;
      return;
    }
    if (previous.current === pathname) return;
    previous.current = pathname;
    target.current?.focus();
  }, [pathname]);
  return target;
}

/**
 * 열려 있는 동안 연 곳을 기억해 두고, 닫히면 **정확히 한 번** 돌려놓습니다.
 *
 * 그 사이에 원래 요소가 DOM에서 사라졌으면 아무 일도 하지 않습니다 — 없는
 * 요소에 focus()를 부르면 포커스가 body로 떨어져 스크린리더가 맥락을 잃습니다.
 */
export function useReturnFocus(open: boolean): void {
  const restore = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      restore.current = document.activeElement as HTMLElement | null;
      return;
    }
    const previous = restore.current;
    restore.current = null;
    if (previous && document.contains(previous)) previous.focus();
  }, [open]);
}

/**
 * 비동기 오류가 처음 나타났을 때 그 알림으로 포커스를 옮깁니다.
 *
 * `episode`는 "같은 오류인가"를 판정하는 값입니다. 같은 값이 유지되는 동안에는
 * 다시 옮기지 않고, 오류가 사라졌다가 다시 나면 새 episode로 한 번 더 옮깁니다.
 */
export function useAsyncErrorFocus(episode: string | null, target: { readonly current: HTMLElement | null }): void {
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (episode === null) {
      announced.current = null;
      return;
    }
    if (announced.current === episode) return;
    announced.current = episode;
    target.current?.focus();
  }, [episode, target]);
}
